import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    afterRenderEffect,
    computed,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    type MatDrawerMode,
} from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { CurrentUserService } from '@core/auth/current-user.service';
import { NavIcon } from '../nav-icon/nav-icon';
import { Sidebar } from '../sidebar/sidebar';

/** Ниже этой ширины сайдбар открывается поверх содержимого, а не занимает место. */
const COMPACT_QUERY = '(width < 900px)';
const STORAGE_KEY = 'nok.shell.nav-collapsed';

function readCollapsed(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

/**
 * Каркас приложения: слева сайдбар с логотипом, названием, управлением и навигацией,
 * справа — router-outlet. Свёрнутость сайдбара запоминается между заходами.
 */
@Component({
    selector: 'app-shell',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.shell--rail]': 'railCollapsed()' },
    imports: [MatSidenavContainer, MatSidenav, MatSidenavContent, RouterOutlet, NavIcon, Sidebar],
    templateUrl: './shell.html',
    styleUrl: './shell.scss',
})
export class Shell {
    private readonly currentUser = inject(CurrentUserService);
    private readonly router = inject(Router);
    private readonly breakpointObserver = inject(BreakpointObserver);

    private readonly collapsed = signal(readCollapsed());
    private readonly mobileOpen = signal(false);
    private readonly container = viewChild.required(MatSidenavContainer);

    protected readonly isCompact = toSignal(
        this.breakpointObserver.observe(COMPACT_QUERY).pipe(map(({ matches }) => matches)),
        { initialValue: window.matchMedia(COMPACT_QUERY).matches },
    );

    protected readonly navMode = computed<MatDrawerMode>(() =>
        this.isCompact() ? 'over' : 'side',
    );
    /** Material скрывает содержимое закрытого дровера, поэтому на широком экране он всегда открыт. */
    protected readonly navOpened = computed(() => !this.isCompact() || this.mobileOpen());
    protected readonly railCollapsed = computed(() => !this.isCompact() && this.collapsed());
    /** Мобильное меню поверх контента: кнопка в шапке показывает состояние для скринридеров. */
    protected readonly mobileNavOpen = this.mobileOpen.asReadonly();

    protected readonly userName = computed(() => this.currentUser.me()?.fullName ?? '');
    protected readonly roleName = computed(() => this.currentUser.me()?.roleName ?? null);

    constructor() {
        // Ширина сайдбара задаётся классом, а Material пересчитывает отступы контента
        // только при открытии/смене режима — синхронизируем после отрисовки.
        afterRenderEffect(() => {
            this.railCollapsed();
            this.container().updateContentMargins();
        });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.mobileOpen.set(false));
    }

    protected toggleNav(): void {
        if (this.isCompact()) {
            this.mobileOpen.update((open) => !open);
            return;
        }

        this.setCollapsed(!this.collapsed());
    }

    /** Закрытие оверлея по Esc или клику по подложке возвращает состояние сигнала. */
    protected closeMobileNav(): void {
        this.mobileOpen.set(false);
    }

    protected logout(): void {
        this.currentUser.logout();
    }

    private setCollapsed(collapsed: boolean): void {
        this.collapsed.set(collapsed);
        try {
            localStorage.setItem(STORAGE_KEY, String(collapsed));
        } catch {
            // Приватный режим или заблокированное хранилище — состояние живёт до перезагрузки.
        }
    }
}
